/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import {useEffect, useRef} from 'react';

interface RefLike {
    current: HTMLElement | null;
}

interface Options {
    open: boolean;
    onClose: VoidFunction;
    /** Repositioning callback — bound to window resize while open and run
     *  once on every open/close transition. */
    onReposition: VoidFunction;
    /** Elements whose inner clicks must NOT dismiss (trigger root + items). */
    insideRefs: RefLike[];
    /** The portaled items element — scrolling inside it never dismisses. */
    itemsRef: RefLike;
}

/**
 * Shared dismissal lifecycle of the portaled dropdowns (BolmateMenu,
 * PortalSelect): an outside mousedown or an outside page-scroll closes the
 * dropdown, a window resize repositions it. Returns the hovering flag to put
 * on the items' mouseenter/mouseleave; it resets automatically on close
 * because the items unmount from under the cursor and mouseleave never fires.
 * The positioning math itself intentionally stays per-control — flip/align
 * (menu) and min-width/resize-observer (select) differ.
 */
export function usePortalDismiss({open, onClose, onReposition, insideRefs, itemsRef}: Options) {
    const hoveringItems = useRef(false);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        const page = document.querySelector('div.page');

        function onClickOutside(evt: MouseEvent) {
            const node = evt.target as Node;
            if (insideRefs.some(ref => ref.current?.contains(node))) return;
            onClose();
        }

        function onScrollOutside(evt: Event) {
            if (hoveringItems.current) return;
            const scrollTarget = evt.target as Node | null;
            if (scrollTarget && itemsRef.current?.contains(scrollTarget)) return;
            onClose();
        }

        if (open) {
            window.addEventListener('resize', onReposition, {signal});
            if (page) page.addEventListener('scroll', onScrollOutside, {signal, capture: true});
            window.setTimeout(() => window.addEventListener('mousedown', onClickOutside, {signal, capture: true}));
        } else {
            hoveringItems.current = false;
        }
        onReposition();
        return () => {
            controller.abort();
        };
        // Handlers close over fresh props each open/close transition.
    }, [open]);

    return hoveringItems;
}
